import { parseProject, type ParseResult } from "@/core/model/serialize.ts";
import type { MediaKind } from "@/core/model/source.ts";
import type { Project, ProjectId } from "@/core/model/index.ts";
import { openDatabase, type Database } from "./idb.ts";
import type { BlobStore } from "./opfs.ts";
import { projectDir } from "./paths.ts";

export const DATABASE_NAME = "mediaknife";
export const DATABASE_VERSION = 1;
const PROJECTS_STORE = "projects";

export type ProjectSummary = {
  readonly id: ProjectId;
  readonly name: string;
  readonly kind: MediaKind;
  readonly updatedAt: string;
  readonly sizeBytes: number;
  /** The source a poster would be stored against; absent for an empty project. */
  readonly sourceId: string | null;
};

export type ProjectRepository = {
  save(project: Project): Promise<void>;
  load(id: ProjectId): Promise<ParseResult | null>;
  list(): Promise<ProjectSummary[]>;
  /** Drops the document and every byte it owns, so no media is left unreachable. */
  remove(id: ProjectId): Promise<void>;
};

export function upgradeDatabase(database: IDBDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    database.createObjectStore(PROJECTS_STORE, { keyPath: "id" });
  }
}

export function openProjectDatabase(): Promise<Database> {
  return openDatabase(DATABASE_NAME, DATABASE_VERSION, upgradeDatabase);
}

export function createProjectRepository(database: Database, blobs: BlobStore): ProjectRepository {
  return {
    save: (project) => database.put(PROJECTS_STORE, project),

    async load(id) {
      const stored = await database.get<unknown>(PROJECTS_STORE, id);
      return stored === undefined ? null : parseProject(stored);
    },

    async list() {
      const stored = await database.getAll<Project>(PROJECTS_STORE);
      return stored
        .map((project) => ({
          id: project.id,
          name: project.name,
          kind: project.kind,
          updatedAt: project.updatedAt,
          sizeBytes: project.sources[0]?.sizeBytes ?? 0,
          sourceId: project.sources[0]?.id ?? null,
        }))
        .toSorted((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },

    async remove(id) {
      await database.remove(PROJECTS_STORE, id);
      await blobs.removeDirectory(projectDir(id));
    },
  };
}
